/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Col,
  Form,
  Grid,
  Input,
  Modal,
  Row,
  Spin,
} from 'antd';
import Table from 'components/Table';
import { onError } from 'utils/handlers';
import { useSelector } from 'react-redux';
import { basePermissioPropTypes } from 'utils/permission/propTypes';
import {
  deleteGiroDeModulo, getGirosDeModulos, patchGiroDeModulo, postGiroDeModulo,
} from 'api/empresas/girosDeModulos';
import { getTiposDeGirosModulos } from 'api/empresas/tiposDeGirosDeModulos';
import FormSubmitControls from './FormSubmitControls';
import Select from './Select';
import ModalDelete from './ModalDelete';

const { useBreakpoint } = Grid;

export const permissionConfig = {
  title: 'Giros de Comercio',
  model: 'girocomercial',
  catalogs: [
    'tipodegirocomercial',
  ],
};
const GirosDeModulos = ({
  expediente_modulo,
  permission,
  justView,
}) => {
  const {
    estadosGlobales,
  } = useSelector((state) => state.catalogs);
  const screen = useBreakpoint();
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [data, setData] = useState([]);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [tipoDeGiroModulos, setTipoDeGiroModulos] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    if (expediente_modulo) {
      const _data = await getGirosDeModulos({ expediente_modulo });
      setData(_data);
    }
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      const [_data, _tiposDeGiros] = await Promise.all([
        await getGirosDeModulos({ expediente_modulo }),
        getTiposDeGirosModulos(),
      ]);
      if (mounted) {
        setData(_data);
        setTipoDeGiroModulos(_tiposDeGiros);

        setLoading(false);
      }
    };
    if (expediente_modulo) fetchAll();
    return () => { mounted = false; };
  }, [expediente_modulo]);

  const onCancel = () => {
    form.resetFields();
    setVisible(false);
    setSelectedRowKeys([]);
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };
  const onClickEdit = () => {
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record?.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      values.expediente_modulo = expediente_modulo;
      const [key] = selectedRowKeys;
      const saved = await (key ? patchGiroDeModulo(key, values) : postGiroDeModulo(values));
      if (saved) {
        onCancel();
        await fetchData();
      }
    } catch (err) {
      onError(err, setLoading);
    } finally {
      setLoading(false);
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const deleted = await deleteGiroDeModulo(key);
    if (deleted) {
      setVisibleAlert(false);
      await fetchData();
    }
  };

  const getWidthModal = () => {
    if (screen.md) {
      return '80%';
    }
    return '100%';
  };

  const columns = [
    {
      titleText: 'Clave',
      key: 'clave',
      dataIndex: 'clave',
      width: 150,
    },
    {
      titleText: 'Tipo de Giros',
      key: 'giro_de_modulos',
      dataIndex: 'giro_de_modulos',
      width: 150,
      catalog: {
        cat: tipoDeGiroModulos,
        use: true,
      },
    },
    {
      titleText: 'Observaciones',
      key: 'observaciones',
      dataIndex: 'observaciones',
      width: 300,
    },
  ];

  const commonProps = {
    cols: columns,
    data: data || [],
    rowSelection,
    handleOnRowClick: justView ? null : handleOnRowClick,
    rowKey: 'id',
    allowExpand: false,
    pagination: false,
    allowSort: false,
  };

  return (
    <Spin
      tip="Cargando..."
      spinning={loading}
    >
      <Table
        {...commonProps}
        {...(justView ? {
          hasAllViewPermission: true,
          allowAdd: false,
          allowEdit: false,
          allowDelete: false,
        } : { permission, controls: { onClickAdd, onClickEdit, onClickDelete } })}
      />

      <Modal
        open={visible}
        closable={false}
        footer={null}
        centered
        width={getWidthModal()}
        title={(
          <FormSubmitControls
            label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Giro de Módulo`}
            onFinish={onFinish}
            onCancel={onCancel}
            loading={loading}
          />
        )}
        keyboard={!loading}
        maskClosable={!loading}
        forceRender
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={() => onFinish()}
          initialValues={{
            estados_globales: 1,
          }}
        >
          <Row gutter={10}>
            {!!selectedRowKeys.length && (
            <Col span={12}>
              <Form.Item
                name="clave"
                label="Clave"
                hasFeedback
              >
                <Input disabled />
              </Form.Item>
            </Col>
            )}
            <Col span={12}>
              <Form.Item
                name="giro_de_modulos"
                label="Tipo de giro"
              >
                <Select dataSource={tipoDeGiroModulos} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="observaciones"
                label="Observaciones"
                hasFeedback
              >
                <Input.TextArea
                  autoSize={{ minRows: 4, maxRows: 4 }}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="estados_globales"
                label="Estado"
                hasFeedback
              >
                <Select
                  disabled={!selectedRowKeys.length}
                  dataSource={estadosGlobales}
                />
              </Form.Item>
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Row>
        </Form>
      </Modal>
      <ModalDelete
        onDelete={deleteItem}
        onCancel={() => setVisibleAlert(false)}
        visible={visibleAlert}
        content="Giro seleccionado"
        loading={loading}
      />
    </Spin>

  );
};

GirosDeModulos.propTypes = {
  expediente_modulo: PropTypes.number,
  permission: basePermissioPropTypes,
  justView: PropTypes.bool,
};

GirosDeModulos.defaultProps = {
  expediente_modulo: null,
  permission: {},
  justView: false,
};

export default GirosDeModulos;
